import React from 'react';
import { useId } from '@reach/auto-id';
import classNames from 'classnames';
import { FieldInputProps } from 'formik';

import {
  useCharactersCount,
  useField,
  useFieldDescription,
} from '../../context/form.context';
import { ErrorMessage } from '../error-message.component';
import { HelpText } from '../help-text.component';
import { FieldLabel } from './field-label.component';

type FieldControlOwnProps = {
  id: string;
  hasError: boolean;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
};

type FieldControlProps = FieldInputProps<any> & FieldControlOwnProps;

export type InputFieldProps = {
  name: string;
  label?: string;
  className?: string;
  id?: string;
  helpText?: string;
  showCharactersCount?: boolean;
  showRequiredIndicator?: boolean;
  children: (field: FieldControlProps) => React.ReactNode;
};

export const InputField = ({
  name,
  label: labelProp,
  className,
  id,
  helpText,
  children,
  showCharactersCount = false,
  showRequiredIndicator = true,
}: InputFieldProps) => {
  const [field, meta] = useField(name);
  const { fieldLabel, required } = useFieldDescription(name);
  const { max } = useCharactersCount(name);
  const label = labelProp ?? fieldLabel;
  const { error, touched } = meta;
  const hasError = Boolean(error && touched);
  const inputId = useId(id)!;
  const helpTextId = useId()!;
  const errorId = useId()!;
  const describingIdsString = classNames({
    [helpTextId]: helpText,
    [errorId]: hasError,
  });

  return (
    <div className={className}>
      {(label || helpText || showCharactersCount) && (
        <div className="flex">
          <FieldLabel
            htmlFor={inputId}
            hasError={hasError}
            showRequiredIndicator={required && showRequiredIndicator}
          >
            {label}
          </FieldLabel>
          {(helpText || showCharactersCount) && <div className="flex-1"></div>}
          {helpText && <HelpText id={helpTextId}>{helpText}</HelpText>}
          {showCharactersCount && !helpText && (
            <HelpText
              id={helpTextId}
            >{`${field.value.length} of ${max}`}</HelpText>
          )}
        </div>
      )}

      {children({
        ...field,
        hasError,
        id: inputId,
        'aria-describedby': describingIdsString || undefined,
        'aria-required': required,
      })}
      {hasError && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
    </div>
  );
};
